import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Acquire the current auth token.
 *
 * @param options.forceRefresh - Ask Amplify to refresh the tokens even if the
 *   cached ones are not yet expired. Used to recover a long-lived session in
 *   place after a 401 (see `fetchWithAuthHeaders`) instead of freezing until a
 *   manual page reload.
 */
export const getAuthToken = async (options?: { forceRefresh?: boolean }) => {
  try {
    const authSession = await fetchAuthSession(
      options?.forceRefresh ? { forceRefresh: true } : undefined,
    );
    const accessToken = authSession.tokens?.accessToken.toString();
    const idToken = authSession.tokens?.idToken?.toString();

    if (accessToken && idToken) {
      return `${accessToken}:${idToken}`;
    }
    return null;
  } catch (error) {
    // A THROW here is a real session-retrieval failure (Cognito/IdP outage,
    // revoked refresh token, Amplify misconfig) — distinct from the normal
    // "no session yet" case, which resolves without throwing. Capture it so a
    // recovery failure is debuggable and an outage is not silently mislabeled
    // as a mass logout. We still return null: callers treat that as
    // "not authenticated".
    Sentry.captureException(error, {
      tags: {
        category: 'auth',
        ['auth_error']: 'session_fetch_failed',
      },
    });
    return null;
  }
};
